import React, { useMemo, useState } from "react";
import { calcFD, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

// Indicative starting rates, Oct 2026 (large banks, e.g. SBI 2-3 yr 6.45%,
// 5-10 yr 6.05%, seniors +0.50%). Tax-saver FDs have a 5-year lock-in.
const FD_PRESETS = {
  "Regular FD": { rate: 6.45 },
  "Tax-Saver FD": { rate: 6.05, months: 60 },
  "Senior Citizen": { rate: 6.95 },
  "Corporate FD": { rate: 7.25 },
};
const fdTypes = Object.keys(FD_PRESETS);

const tenureLabel = (months) => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} ${m === 1 ? "Month" : "Months"}`;
  if (m === 0) return `${y} ${y === 1 ? "Year" : "Years"}`;
  return `${y}y ${m}m`;
};

export const FDTab = () => {
  const [amount, setAmount] = useState(100000);
  const [rate, setRate] = useState(FD_PRESETS["Regular FD"].rate);
  const [months, setMonths] = useState(36);
  const [fdType, setFdType] = useState("Regular FD");

  const chooseType = (type) => {
    setFdType(type);
    setRate(FD_PRESETS[type].rate);
    if (FD_PRESETS[type].months) setMonths(FD_PRESETS[type].months);
  };

  const result = useMemo(() => calcFD(amount, rate, months), [amount, rate, months]);
  const animKey = `fd-${Math.round(result.maturity)}-${amount}-${rate}-${months}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Deposit Amount"
          valueText={`Rs. ${formatINR(amount)}`}
          valueTestId="fd-amount-value"
          finePrint={<><span>Rs.1,000</span><span>Rs.50,00,000</span></>}
        >
          <Slider value={amount} min={1000} max={5000000} step={1000} onChange={setAmount} ariaLabel="FD amount" />
        </InputRow>

        <InputRow
          label="Annual Interest Rate"
          valueText={`${rate.toFixed(2)}% per annum`}
          valueTestId="fd-rate-value"
          finePrint={<><span>3%</span><span>10% per annum</span></>}
        >
          <Slider value={rate} min={3} max={10} step={0.05} onChange={setRate} ariaLabel="FD rate" />
        </InputRow>

        <InputRow
          label="Tenure"
          valueText={`${tenureLabel(months)} (${months} Months)`}
          valueTestId="fd-tenure-value"
          finePrint={<><span>6 months</span><span>10 years</span></>}
        >
          <Slider value={months} min={6} max={120} step={3} onChange={setMonths} ariaLabel="FD tenure" />
        </InputRow>

        <div>
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>FD Type</div>
          <Pills items={fdTypes} value={fdType} onChange={chooseType} prefix="fd-type" />
          <p className="mt-3" style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }}>
            Interest compounded quarterly (standard Indian FD). Each type sets a typical October 2026 rate; banks'
            actual rates vary by tenure, so adjust the slider to match your bank. Tax-saver FDs have a 5-year lock-in.
          </p>
        </div>
      </div>

      <RightPanel testId="fd-results">
        <HeroValue label="Maturity Value" value={`Rs. ${formatINR(result.maturity)}`} animKey={animKey} testId="fd-maturity-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Deposit Amount" value={`Rs. ${formatINR(amount)}`} testId="fd-row-principal" />
          <ResultRow label="Interest Earned" value={`Rs. ${formatINR(result.interest)}`} valueColor="#1D9E75" testId="fd-row-interest" />
          <ResultRow label="Total Value" value={`Rs. ${formatINR(result.maturity)}`} testId="fd-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.principalPct}
          rightPct={result.interestPct}
          leftLabel="Principal"
          rightLabel="Interest"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="fd-split-bar"
        />
      </RightPanel>
    </div>
  );
};
